"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { authService } from "@/lib/api/services";
import { getErrorMessage } from "@/lib/utils/error-handler";
import {
  type AuthFormAppearance,
  loginInputClass,
  loginSubmitClass,
} from "./loginAppearance";
import { cn } from "@/lib/utils";

interface AuthResetPasswordFormProps {
  phone: string;
  code: string;
  onSuccess: () => void;
  onBack: () => void;
  showBackButton?: boolean;
  appearance?: AuthFormAppearance;
}

export function AuthResetPasswordForm({
  phone,
  code,
  onSuccess,
  appearance = "default",
}: AuthResetPasswordFormProps) {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formSchema = z
    .object({
      password: z
        .string()
        .min(1, t("auth.passwordRequired"))
        .min(6, t("auth.passwordMinLength")),
      confirmPassword: z.string().min(1, t("auth.confirmPasswordRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.passwordMismatch"),
      path: ["confirmPassword"],
    });

  type ResetFormValues = z.infer<typeof formSchema>;

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onTouched",
  });

  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");
  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;
  const passwordsDontMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const response = await authService.resetPassword({
        code,
        mobile: phone,
        password: values.password,
      });

      if (response.success) {
        onSuccess();
      } else {
        form.setError("root", {
          message: response.message || response.error || t("auth.invalidCode"),
        });
      }
    } catch (err: unknown) {
      const message = getErrorMessage(err, t("auth.invalidCode"), t);
      form.setError("root", { message });
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.newPassword")}</FormLabel>
                <FormControl>
                  <div className="relative !py-0">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn("h-12 pe-10", loginInputClass(appearance))}
                      disabled={isSubmitting}
                      autoFocus
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.confirmNewPassword")}</FormLabel>
                <FormControl>
                  <div className="relative !py-0">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn(
                        "h-12 pe-10 transition-colors",
                        loginInputClass(appearance),
                        passwordsMatch && "border-green-500 bg-green-50",
                        passwordsDontMatch && "border-red-500 bg-red-50",
                      )}
                      disabled={isSubmitting}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                {passwordsMatch && (
                  <p className="text-sm text-green-600 flex items-center gap-1 font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("auth.passwordsMatch")}
                  </p>
                )}
                {passwordsDontMatch && (
                  <p className="text-sm text-red-500 flex items-center gap-1 font-medium">
                    <XCircle className="h-4 w-4" />
                    {t("auth.passwordMismatch")}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}

          {form.formState.isSubmitSuccessful && (
            <Alert>
              <AlertDescription>
                {t("auth.passwordResetSuccess")}
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className={cn(
              appearance === "authPage"
                ? loginSubmitClass(appearance)
                : "w-full h-12 rounded-xl font-bold",
            )}
            disabled={isSubmitting || passwordsDontMatch}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin me-2" />
                {t("common.loading")}
              </>
            ) : (
              t("auth.changePassword")
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
